/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file displayText.js
 * @description
 * Pure helper that turns any backend "name-like" value into a plain string that
 * is always safe to render as a React child. Backend names are inconsistent:
 * `userInfo.name` is `{first, last}` on online/driver orders but a string on
 * others, and a raw object rendered inside <Text> crashes the whole screen with
 * "Objects are not valid as a React child" (React #31).
 */

/**
 * Convert a name-like value to a display string. Never throws, never returns an object.
 * @param {*} value string | number | {first,last} | {name} | {label} | null
 * @param {number} [depth=0] Internal recursion guard.
 * @returns {string} Trimmed text ('' when nothing renderable).
 */
export const toDisplayText = (value, depth = 0) => {
    if (value === null || value === undefined || depth > 3) {
        return '';
    }

    if (typeof value === 'string') {
        return value.trim();
    }

    if (typeof value === 'number' || typeof value === 'bigint') {
        return String(value);
    }

    if (typeof value !== 'object') {
        return '';
    }

    if (value.first !== undefined || value.last !== undefined) {
        return [toDisplayText(value.first, depth + 1), toDisplayText(value.last, depth + 1)]
            .filter(Boolean)
            .join(' ');
    }

    return toDisplayText(value.name ?? value.label ?? value.code, depth + 1);
};

export default toDisplayText;
